import React, { useRef, useState, useEffect, useCallback } from "react"
import "./appShowCase.css"

/**
 * Images du store qui défilent horizontalement.
 * Les images se suivent sans espace (certaines commencent d'un côté et finissent sur la suivante).
 *
 * Usage:
 *   <AppShowcase images={["/img/store1.png", "/img/store2.png", ...]} />
 *
 * Tu peux aussi passer des imports :
 *   import s1 from "./assets/store1.png"
 *   <AppShowcase images={[s1, s2, s3, ...]} />
 */

const AppShowcase = ({ images = [] }) => {
    const trackRef = useRef(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(true)
    const [activeIndex, setActiveIndex] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const dragState = useRef({ startX: 0, scrollLeft: 0 })

    const imageCount = images.length

    /* ── Scroll state check ── */
    const updateScrollState = useCallback(() => {
        const el = trackRef.current
        if (!el) return
        const tolerance = 2
        setCanScrollLeft(el.scrollLeft > tolerance)
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - tolerance)

        // Determine active index from scroll position
        if (imageCount > 0) {
            const imageWidth = el.scrollWidth / imageCount
            const idx = Math.round(el.scrollLeft / imageWidth)
            setActiveIndex(Math.min(idx, imageCount - 1))
        }
    }, [imageCount])

    useEffect(() => {
        const el = trackRef.current
        if (!el) return
        updateScrollState()
        el.addEventListener("scroll", updateScrollState, { passive: true })
        window.addEventListener("resize", updateScrollState)
        return () => {
            el.removeEventListener("scroll", updateScrollState)
            window.removeEventListener("resize", updateScrollState)
        }
    }, [updateScrollState])

    /* ── Arrow navigation ── */
    const scroll = (direction) => {
        const el = trackRef.current
        if (!el) return
        const scrollAmount = el.clientWidth * 0.7
        el.scrollBy({
            left: direction === "left" ? -scrollAmount : scrollAmount,
            behavior: "smooth",
        })
    }

    /* ── Dot navigation ── */
    const scrollToIndex = (index) => {
        const el = trackRef.current
        if (!el || imageCount === 0) return
        const imageWidth = el.scrollWidth / imageCount
        el.scrollTo({
            left: imageWidth * index,
            behavior: "smooth",
        })
    }

    /* ── Mouse drag to scroll ── */
    const onMouseDown = (e) => {
        const el = trackRef.current
        if (!el) return
        setIsDragging(true)
        dragState.current.startX = e.pageX - el.offsetLeft
        dragState.current.scrollLeft = el.scrollLeft
        el.style.cursor = "grabbing"
    }

    const onMouseMove = (e) => {
        if (!isDragging) return
        e.preventDefault()
        const el = trackRef.current
        if (!el) return
        const x = e.pageX - el.offsetLeft
        const walk = (x - dragState.current.startX) * 1.5
        el.scrollLeft = dragState.current.scrollLeft - walk
    }

    const onMouseUp = () => {
        setIsDragging(false)
        if (trackRef.current) {
            trackRef.current.style.cursor = "grab"
        }
    }

    if (imageCount === 0) return null

    return (
        <section className="showcase-section">
            <div className="showcase-header">
                <span className="lp-section-label">Aperçu de l'application</span>
                <h2 className="lp-section-title">JustNow en images</h2>
                <p className="lp-section-subtitle">
                    Découvrez l'expérience JustNow à travers les captures de l'application.
                    Faites défiler pour explorer toutes les fonctionnalités.
                </p>
            </div>

            <div className="showcase-viewport">
                {/* Left arrow */}
                {canScrollLeft && (
                    <button
                        className="showcase-arrow showcase-arrow--left"
                        onClick={() => scroll("left")}
                        aria-label="Défiler à gauche"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                )}

                {/* Image track */}
                <div
                    className={`showcase-track ${isDragging ? "is-dragging" : ""}`}
                    ref={trackRef}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                >
                    {images.map((src, i) => (
                        <div className="showcase-slide" key={i}>
                            <img
                                src={src}
                                alt={`Capture JustNow ${i + 1}`}
                                draggable="false"
                            />
                        </div>
                    ))}
                </div>

                {/* Right arrow */}
                {canScrollRight && (
                    <button
                        className="showcase-arrow showcase-arrow--right"
                        onClick={() => scroll("right")}
                        aria-label="Défiler à droite"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Dot indicators */}
            {imageCount > 1 && (
                <div className="showcase-dots">
                    {images.map((_, i) => (
                        <button
                            key={i}
                            className={`showcase-dot ${i === activeIndex ? "active" : ""}`}
                            onClick={() => scrollToIndex(i)}
                            aria-label={`Aller à l'image ${i + 1}`}
                        />
                    ))}
                </div>
            )}
        </section>
    )
}

export default AppShowcase
